import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from "luxon";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useCart } from '../contexts/CartContext';
import {
  CHAMPS_APPRENANT,
  MAX_TELEPHONES_URGENCE,
  COURRIEL_VALIDE,
  compteur,
  erreurPendantSaisie,
  erreurTelephone,
  erreursFormulaireApprenant,
  apprenantPourServeur,
  verifierAupresDuServeur,
  erreursServeurParChamp,
  defilerVersPremiereErreur,
  listeTelephones,
  texteTelephones,
} from '../utils/reglesInscription';

// Configuration globale de Luxon pour utiliser UTC
import { Settings } from "luxon";
Settings.defaultZone = "utc";

const steps = ['Informations', 'Validation', 'Confirmation'];

const provinces = [
  { value: 'QC', label: 'Québec' },
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'Colombie-Britannique' },
  { value: 'AB', label: 'Alberta' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NS', label: 'Nouvelle-Écosse' },
  { value: 'NB', label: 'Nouveau-Brunswick' },
  { value: 'NL', label: 'Terre-Neuve-et-Labrador' },
  { value: 'PE', label: 'Île-du-Prince-Édouard' },
  { value: 'NT', label: 'Territoires du Nord-Ouest' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' },
];

// Mapping des provinces pour l'autocomplétion
const provinceMapping = {
  'Quebec': 'QC',
  'Québec': 'QC',
  'Ontario': 'ON',
  'British Columbia': 'BC',
  'Alberta': 'AB',
  'Manitoba': 'MB',
  'Saskatchewan': 'SK',
  'Nova Scotia': 'NS',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Prince Edward Island': 'PE',
  'Northwest Territories': 'NT',
  'Nunavut': 'NU',
  'Yukon': 'YT'
};

const InscriptionIndividuelle = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { formationId, sessionId, formationTitre, sessionInfo, formation } = location.state || {};
  const formTopRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tempProvince, setTempProvince] = useState('');
  const [usingCustomProvince, setUsingCustomProvince] = useState(false);
  const [formationDetails, setFormationDetails] = useState(null);

  // États pour l'autocomplétion d'adresse
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    // Informations de l'apprenant (celui qui suit la formation)
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adressepostale: '',
    numero_appartement: '',
    ville: '',
    province: '',
    code_postal: '',
    date_naissance: '',
    
    // NOUVEAU : Informations optionnelles pour enfants (si formation non pour soi-même)
    // Téléphones d'urgence : une case par numéro, jusqu'à 3.
    telephone_urgence: [''],
    allergies: '',
    besoins_particuliers: '',
    ecole_frequentee: '',
    
    // Informations du payeur (celui qui paie)
    nom_payeur: '',
    prenom_payeur: '',
    
    accepte: false
  });

  const [formErrors, setFormErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Charger les détails de la formation au chargement
  useEffect(() => {
    if (!formationId || !sessionId) {
      navigate('/formations');
      return;
    }

    const fetchFormationDetails = async () => {
      try {
        const response = await axios.get(`${API_URL}/formations/${formationId}`);
        if (response.data.success) {
          setFormationDetails(response.data.data);
        }
      } catch (error) {
        console.error('Erreur chargement formation:', error);
      }
    };

    fetchFormationDetails();
  }, [formationId, sessionId, navigate, API_URL]);

  // Forcer le scroll sur mobile pour bien positionner le formulaire
  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    if (!isMobile) return;

    const timeoutId = setTimeout(() => {
      if (formTopRef.current?.scrollIntoView) {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [activeStep]);

  // Déterminer si la formation est pour soi-même
  const isForSelf = formationDetails?.is_for_self || formation?.is_for_self || false;

  // Debounce function pour l'autocomplétion
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Fonction de recherche d'adresse avec Photon (gratuit, pas de CORS)
  const searchAddress = async (searchTerm) => {
    if (searchTerm.length < 5) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);

    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(searchTerm + ', Québec')}&limit=5&lang=fr`
      );

      if (!response.ok) throw new Error('Erreur réseau');

      const data = await response.json();

      const suggestions = (data.features || []).map(feature => {
        const props = feature.properties || {};
        const ville = props.city || props.town || props.village || '';
        const province = props.state || 'Québec';
        
        let adresse = '';
        if (props.street) {
          adresse = `${props.housenumber || ''} ${props.street}`.trim();
        } else if (props.name) {
          adresse = props.name;
        } else {
          adresse = props.display_name?.split(',')[0] || '';
        }

        return {
          id: props.osm_id || Math.random(),
          display_name: `${adresse}, ${ville}, ${province} ${props.postcode || ''}`.trim(),
          adresse: adresse,
          ville: ville,
          province: province,
          province_code: provinceMapping[province] || province,
          code_postal: props.postcode || ''
        };
      }).filter(s => s.ville);

      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Erreur recherche adresse:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const debouncedSearch = debounce(searchAddress, 500);

  // Gestionnaire pour le champ d'adresse avec autocomplétion
  const handleAddressChange = (e) => {
    const value = e.target.value;
    
    // Mettre à jour le champ adressepostale
    setFormData(prev => ({
      ...prev,
      adressepostale: value
    }));

    // Erreur à jour pendant la saisie (limite de longueur)
    setFormErrors(prev => ({
      ...prev,
      adressepostale: erreurPendantSaisie('adressepostale', value)
    }));

    // Lancer la recherche
    if (value.length >= 5) {
      debouncedSearch(value);
    } else {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  // Sélectionner une suggestion d'adresse
  const selectAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      adressepostale: suggestion.adresse,
      ville: suggestion.ville,
      province: suggestion.province_code,
      code_postal: suggestion.code_postal || prev.code_postal
    }));

    setShowAddressSuggestions(false);
    setAddressSuggestions([]);

    // Si la province sélectionnée n'est pas dans la liste, activer le mode custom
    if (!provinces.some(p => p.value === suggestion.province_code)) {
      setTempProvince(suggestion.province);
      setUsingCustomProvince(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Le champ devient rouge dès qu'il dépasse sa limite, pendant la saisie ;
    // sinon son ancienne erreur disparaît.
    const erreur = type === 'checkbox' ? '' : erreurPendantSaisie(name, value);
    if (erreur || formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: erreur
      }));
    }
  };

  // Courriel incomplet : signalé en quittant le champ, pas à chaque lettre.
  const handleEmailBlur = () => {
    const email = formData.email.trim();
    if (email && !formErrors.email && !COURRIEL_VALIDE.test(email)) {
      setFormErrors(prev => ({ ...prev, email: 'Courriel : adresse invalide (exemple : nom@domaine.ca)' }));
    }
  };

  // ── Téléphones d'urgence (une case par numéro) ──
  const changerTelephoneUrgence = (index) => (e) => {
    const valeur = e.target.value;
    setFormData(prev => ({
      ...prev,
      telephone_urgence: prev.telephone_urgence.map((n, i) => (i === index ? valeur : n))
    }));
    const nom = formData.telephone_urgence.length > 1 ? `Téléphone d'urgence n° ${index + 1}` : "Téléphone d'urgence";
    // Pendant la saisie, seule la longueur est vérifiée : le reste attend « Suivant ».
    const erreur = valeur.trim().length > 20 ? erreurTelephone(valeur, nom) : '';
    setFormErrors(prev => ({ ...prev, [`telephone_urgence_${index}`]: erreur }));
  };

  const ajouterTelephoneUrgence = () => {
    if (formData.telephone_urgence.length >= MAX_TELEPHONES_URGENCE) return;
    setFormData(prev => ({ ...prev, telephone_urgence: [...prev.telephone_urgence, ''] }));
  };

  const retirerTelephoneUrgence = (index) => {
    setFormData(prev => ({
      ...prev,
      telephone_urgence: prev.telephone_urgence.filter((_, i) => i !== index)
    }));
    // Les erreurs suivent les cases restantes.
    setFormErrors(prev => {
      const suivantes = { ...prev };
      for (let i = index; i < MAX_TELEPHONES_URGENCE; i += 1) {
        suivantes[`telephone_urgence_${i}`] = prev[`telephone_urgence_${i + 1}`] || '';
      }
      return suivantes;
    });
  };

  const handleProvinceChange = (e) => {
    const value = e.target.value;
    
    if (value === 'custom') {
      setTempProvince(formData.province);
      setUsingCustomProvince(true);
    } else {
      setUsingCustomProvince(false);
      setFormData(prev => ({
        ...prev,
        province: value
      }));
    }
    
    if (formErrors.province) {
      setFormErrors(prev => ({
        ...prev,
        province: ''
      }));
    }
  };

  const handleCustomProvinceChange = (e) => {
    setFormData(prev => ({
      ...prev,
      province: e.target.value
    }));
    setFormErrors(prev => ({ ...prev, province: erreurPendantSaisie('province', e.target.value) }));
  };

  const handleCancelCustomProvince = () => {
    setFormData(prev => ({
      ...prev,
      province: tempProvince
    }));
    setUsingCustomProvince(false);
  };

  // Champs présents dans le formulaire : payeur et informations de l'enfant
  // seulement quand l'inscription est faite pour quelqu'un d'autre.
  const champsDuFormulaire = Object.keys(CHAMPS_APPRENANT).filter((champ) =>
    !isForSelf || !['nom_payeur', 'prenom_payeur', 'allergies', 'besoins_particuliers', 'ecole_frequentee'].includes(champ)
  );

  // Texte sous un champ : son erreur, sinon le compteur de caractères quand
  // on approche de la limite, sinon l'aide habituelle.
  const aide = (champ, defaut = '') =>
    formErrors[champ] || compteur(formData[champ], CHAMPS_APPRENANT[champ].max) || defaut;

  const validateStep1 = () => {
    const errors = erreursFormulaireApprenant(formData, { pourUnTiers: !isForSelf, champs: champsDuFormulaire });
    if (!formData.accepte) errors.accepte = 'Vous devez accepter les conditions';

    setFormErrors(errors);
    const valide = Object.keys(errors).length === 0;
    if (!valide) defilerVersPremiereErreur();
    return valide;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setError('');
      if (validateStep1()) {
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate(-1);
    }
  };

  // --- Fonction pour formater la date avec Luxon ---
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const dt = DateTime.fromISO(dateString);
      return dt.toFormat('dd/MM/yyyy');
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Vérifier si une inscription existe déjà avec le même email pour cette session (panier)
      const existingInscriptionInCart = cartItems?.some(
        item => item.formData.email === formData.email && item.sessionId === sessionId
      );

      if (existingInscriptionInCart) {
        setError(
    <>
      Une inscription avec cette adresse courriel existe déjà pour cette session dans le panier. 
      Pour inscrire un autre apprenant à la même formation, chaque inscription doit être faite avec une{' '}
      <strong>adresse courriel différente</strong>.
    </>
  );
       
        setLoading(false);
        return;
      }

      // Vérifier si une inscription existe déjà dans la base de données pour cette session avec le même email
      try {
        // Point d'entrée public dédié : ne renvoie qu'un booléen, contrairement
        // à l'ancien appel qui rapatriait toute la liste des participants.
        const response = await axios.get(
          `${API_URL}/inscriptions/check-email`,
          { params: { session_id: sessionId, email: formData.email } }
        );

        if (response.data.success && response.data.data) {
          const hasDuplicateEmail = response.data.data.exists;

          if (hasDuplicateEmail) {
           setError(
    <>
      Cette adresse courriel est déjà utilisée pour une inscription à cette session. 
      Pour inscrire un autre apprenant à la même formation, veuillez utiliser une{' '}
      <strong>adresse courriel différente</strong>.
    </>
  );
            setLoading(false);
            return;
          }
        }
      } catch (dbCheckError) {
        console.error('Erreur lors de la vérification en base de données:', dbCheckError);
        // On continue même si la vérification échoue, car c'est une vérification supplémentaire
      }

      // Vérification par le serveur AVANT l'ajout au panier : l'inscription
      // n'entre dans le panier que si elle pourrait réellement être enregistrée.
      let verification;
      try {
        verification = await verifierAupresDuServeur(API_URL, [{
          apprenant: apprenantPourServeur(formData, isForSelf),
          session_id: sessionId,
          pour_un_tiers: !isForSelf,
          formationTitre,
        }]);
      } catch (erreurVerification) {
        setError(erreurVerification.message);
        setLoading(false);
        return;
      }

      if (!verification.valide) {
        const { parChamp, autres } = erreursServeurParChamp(verification.articles[0]);
        if (Object.keys(parChamp).length > 0) {
          // Retour au formulaire, sur le premier champ à corriger.
          setFormErrors(parChamp);
          setError(autres.length ? autres.join(' ') : 'Certaines informations doivent être corrigées.');
          setActiveStep(0);
          defilerVersPremiereErreur();
        } else {
          setError(autres.join(' '));
        }
        setLoading(false);
        return;
      }

      // Ajouter le formulaire au panier
      const cartItem = {
        formationId,
        sessionId,
        formationTitre,
        sessionInfo,
        formation: formationDetails, // Utiliser formationDetails qui contient le tarif
        formData: { ...formData, telephone_urgence: listeTelephones(formData.telephone_urgence) },
        isForSelf
      };

      addToCart(cartItem);

      // Préinscription discrète : on enregistre le "lead" et on notifie l'admin
      // dès l'ajout au panier (sans bloquer l'utilisateur ni afficher d'erreur).
      axios.post(`${API_URL}/preinscriptions`, {
        ...formData,
        telephone_urgence: listeTelephones(formData.telephone_urgence),
        is_for_self: isForSelf,
        formation_id: formationId,
        formation_titre: formationTitre,
        session_id: sessionId,
        session_ville: sessionInfo?.ville,
        session_date_debut: sessionInfo?.date_debut,
        session_date_fin: sessionInfo?.date_fin,
        tarif: formationDetails?.tarif,
      }).catch((preErr) => console.error('Préinscription (non bloquant):', preErr));

      // Afficher le message de confirmation
      setActiveStep(2);
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de l\'ajout au panier');
    } finally {
      setLoading(false);
    }
  };

  const formatCodePostal = (value) => {
    let formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (formatted.length > 3) {
      formatted = formatted.substring(0, 3) + ' ' + formatted.substring(3, 6);
    }
    return formatted;
  };

  const handleCodePostalChange = (e) => {
    const formatted = formatCodePostal(e.target.value);
    setFormData(prev => ({
      ...prev,
      code_postal: formatted
    }));
    
    if (formErrors.code_postal) {
      setFormErrors(prev => ({
        ...prev,
        code_postal: ''
      }));
    }
  };

  // Styles
  const sectionStyle = {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px'
  };

  const sectionTitleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    color: '#333',
    fontWeight: 600
  };

  return (
    <Container ref={formTopRef} maxWidth="md" sx={{ py: 4 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            {isForSelf ? 'Vos informations personnelles' : 'Informations d\'inscription'}
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* SECTION APPRENANT */}
            <Box sx={sectionStyle}>
              <Typography variant="h6" sx={sectionTitleStyle}>
                <PersonIcon color="primary" />
                {isForSelf ? 'Vos informations' : 'Informations de l\'apprenant (personne qui suit la formation)'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <TextField
                    required
                    fullWidth
                    label={isForSelf ? "Votre nom" : "Nom de l'apprenant"}
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    error={!!formErrors.nom}
                    helperText={aide('nom')}
                  />
                  <TextField
                    required
                    fullWidth
                    label={isForSelf ? "Votre prénom" : "Prénom de l'apprenant"}
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    error={!!formErrors.prenom}
                    helperText={aide('prenom')}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <TextField
                    required
                    fullWidth
                    label={isForSelf ? "Votre email" : "Email de l'apprenant"}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={aide('email')}
                    onBlur={handleEmailBlur}
                  />
                  <TextField
                    required
                    fullWidth
                    label={isForSelf ? "Votre téléphone" : "Téléphone de l'apprenant"}
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    error={!!formErrors.telephone}
                    helperText={aide('telephone')}
                  />
                </Box>
                
                {/* Champ d'adresse avec autocomplétion */}
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    required
                    fullWidth
                    label="Adresse postale"
                    name="adressepostale"
                    value={formData.adressepostale}
                    onChange={handleAddressChange}
                    onFocus={() => {
                      if (addressSuggestions.length > 0) {
                        setShowAddressSuggestions(true);
                      }
                    }}
                    error={!!formErrors.adressepostale}
                    helperText={aide('adressepostale', 'Commencez à taper votre adresse...')}
                    InputProps={{
                      endAdornment: isSearchingAddress && (
                        <CircularProgress size={20} />
                      ),
                    }}
                  />
                  
                  {/* Suggestions d'adresses */}
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <ClickAwayListener onClickAway={() => setShowAddressSuggestions(false)}>
                      <Paper
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: 300,
                          overflowY: 'auto',
                          mt: 0.5,
                          boxShadow: 3
                        }}
                      >
                        <List>
                          {addressSuggestions.map((suggestion) => (
                            <ListItem
                              key={suggestion.id}
                              onClick={() => selectAddress(suggestion)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                },
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                              }}
                            >
                              <LocationOnIcon color="action" sx={{ mt: 0.5, fontSize: 20 }} />
                              <ListItemText
                                primary={suggestion.display_name.split(',')[0]}
                                secondary={
                                  <React.Fragment>
                                    <Typography component="span" variant="body2" color="text.primary">
                                      {suggestion.ville && `${suggestion.ville}, `}
                                      {suggestion.province && `${suggestion.province} `}
                                      {suggestion.code_postal && suggestion.code_postal}
                                    </Typography>
                                  </React.Fragment>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <TextField
                    fullWidth
                    label="Appartement/Bureau"
                    name="numero_appartement"
                    value={formData.numero_appartement}
                    onChange={handleChange}
                    error={!!formErrors.numero_appartement}
                    helperText={aide('numero_appartement')}
                  />
                  <TextField
                    required
                    fullWidth
                    label="Ville"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    error={!!formErrors.ville}
                    helperText={aide('ville')}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <FormControl fullWidth error={!!formErrors.province}>
                    <InputLabel id="province-label">Province *</InputLabel>
                    {!usingCustomProvince ? (
                      <Select
                        labelId="province-label"
                        name="province"
                        value={formData.province}
                        onChange={handleProvinceChange}
                        label="Province *"
                        MenuProps={{
                          disableAutoFocusItem: false,
                          slotProps: {
                            paper: {
                              'data-testid': 'province-menu'
                            }
                          }
                        }}
                      >
                        {provinces.map((province) => (
                          <MenuItem key={province.value} value={province.value}>
                            {province.label}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">
                          <em>Autre (spécifier)</em>
                        </MenuItem>
                      </Select>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <TextField
                          fullWidth
                          label="Province"
                          value={formData.province}
                          onChange={handleCustomProvinceChange}
                          placeholder="Entrez votre province"
                          error={!!formErrors.province}
                        />
                        <IconButton
                          onClick={handleCancelCustomProvince}
                          color="error"
                          title="Revenir au menu des provinces"
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    )}
                    {formErrors.province && (
                      <FormHelperText>{formErrors.province}</FormHelperText>
                    )}
                  </FormControl>
                  
                  <TextField
                    required
                    fullWidth
                    label="Code postal"
                    name="code_postal"
                    value={formData.code_postal}
                    onChange={handleCodePostalChange}
                    error={!!formErrors.code_postal}
                    helperText={formErrors.code_postal || 'Format: A1A 1A1'}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
                
                <TextField
                  required
                  fullWidth
                  label={isForSelf ? "Votre date de naissance" : "Date de naissance de l'apprenant"}
                  name="date_naissance"
                  type="date"
                  value={formData.date_naissance}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.date_naissance}
                  helperText={formErrors.date_naissance}
                />
              </Box>
            </Box>

            {/* SECTION PAYEUR - visible seulement si ce n'est PAS pour soi-même */}
            {!isForSelf && (
              <Box sx={sectionStyle}>
                <Typography variant="h6" sx={sectionTitleStyle}>
                  <PaymentIcon color="primary" />
                  Informations du payeur (celui qui effectue le paiement)
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <TextField
                    fullWidth
                    required
                    label="Nom du payeur"
                    name="nom_payeur"
                    value={formData.nom_payeur}
                    onChange={handleChange}
                    error={!!formErrors.nom_payeur}
                    helperText={aide('nom_payeur')}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Prénom du payeur"
                    name="prenom_payeur"
                    value={formData.prenom_payeur}
                    onChange={handleChange}
                    error={!!formErrors.prenom_payeur}
                    helperText={aide('prenom_payeur')}
                  />
                </Box>
                
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
                  Le payeur est la personne qui sera contactée pour finaliser le paiement.
                </Typography>
              </Box>
            )}

            {/* NOUVEAU : SECTION TÉLÉPHONE D'URGENCE (obligatoire, visible seulement si ce n'est PAS pour soi-même) */}
            {!isForSelf && (
              <Box sx={sectionStyle}>
                <Typography variant="h6" sx={sectionTitleStyle}>
                  <InfoIcon color="primary" />
                  Numéro de téléphone d'urgence
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Un seul numéro par case. Ajoutez une case pour chaque autre numéro à appeler
                  ({MAX_TELEPHONES_URGENCE} au maximum).
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.telephone_urgence.map((numero, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        required={index === 0}
                        label={index === 0 ? "Numéro de téléphone d'urgence à appeler" : `Autre numéro d'urgence (${index + 1})`}
                        name={`telephone_urgence_${index}`}
                        value={numero}
                        onChange={changerTelephoneUrgence(index)}
                        error={!!formErrors[`telephone_urgence_${index}`]}
                        helperText={formErrors[`telephone_urgence_${index}`] || (index === 0 ? 'Exemple : (514) 123-4567' : '')}
                        inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                      />
                      {index > 0 && (
                        <IconButton
                          onClick={() => retirerTelephoneUrgence(index)}
                          aria-label="Retirer ce numéro"
                          title="Retirer ce numéro"
                          sx={{ mt: 1 }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>

                {formData.telephone_urgence.length < MAX_TELEPHONES_URGENCE && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={ajouterTelephoneUrgence}
                    sx={{ mt: 2, textTransform: 'none' }}
                  >
                    Ajouter un autre numéro
                  </Button>
                )}
              </Box>
            )}

            {/* NOUVEAU : SECTION INFORMATIONS SUPPLÉMENTAIRES (optionnel, visible seulement si ce n'est PAS pour soi-même) */}
            {!isForSelf && (
              <Box sx={sectionStyle}>
                <Typography variant="h6" sx={sectionTitleStyle}>
                  <InfoIcon color="primary" />
                  Informations supplémentaires (optionnel)
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2.5, fontStyle: 'italic' }}>
                  Veuillez remplir ces informations pour nous aider à mieux accompagner le participant pendant la formation.
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Allergies (le cas échéant)"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  error={!!formErrors.allergies}
                  helperText={aide('allergies')}
                  placeholder="Ex: Arachides, noix, produits laitiers..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Besoins particuliers ou informations importantes"
                  name="besoins_particuliers"
                  value={formData.besoins_particuliers}
                  onChange={handleChange}
                  error={!!formErrors.besoins_particuliers}
                  helperText={aide('besoins_particuliers')}
                  placeholder="Ex: Allergie sonore, besoin de pauses fréquentes, autre condition à connaître..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="École fréquentée (si applicable)"
                  name="ecole_frequentee"
                  value={formData.ecole_frequentee}
                  onChange={handleChange}
                  error={!!formErrors.ecole_frequentee}
                  helperText={aide('ecole_frequentee')}
                  placeholder="Ex: École primaire Abc, Polyvalente XYZ..."
                />
              </Box>
            )}
            
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: formErrors.accepte ? 'error.main' : 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="accepte"
                    checked={formData.accepte}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    J'accepte les{' '}
                    <MuiLink
                      component={Link}
                      to="/conditions-inscription"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
                    >
                      conditions générales d'inscription
                    </MuiLink>
                    {' '}et la{' '}
                    <MuiLink
                      component={Link}
                      to="/declaration-confidentialite"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
                    >
                      politique de confidentialité
                    </MuiLink>
                  </Typography>
                }
              />
              {formErrors.accepte && (
                <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
                  {formErrors.accepte}
                </FormHelperText>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button 
              onClick={handleBack} 
              startIcon={<ArrowBackIcon />}
              variant="outlined"
            >
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{ minWidth: 150 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Suivant'}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Récapitulatif
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
            
            {/* Récapitulatif apprenant */}
            <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                {isForSelf ? 'Vos informations' : 'Informations de l\'apprenant'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Nom complet</Typography>
                  <Typography variant="body1">{formData.nom} {formData.prenom}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{formData.email}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Téléphone</Typography>
                  <Typography variant="body1">{formData.telephone}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Date de naissance</Typography>
                  <Typography variant="body1">{formatDateForDisplay(formData.date_naissance)}</Typography>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" color="textSecondary">Adresse</Typography>
                  <Typography variant="body1">
                    {formData.adressepostale}
                    {formData.numero_appartement && `, App. ${formData.numero_appartement}`}
                    <br />
                    {formData.ville}, {formData.province} {formData.code_postal}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Récapitulatif payeur - visible seulement si ce n'est PAS pour soi-même */}
            {!isForSelf && (
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  Informations du payeur
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Nom du payeur</Typography>
                    <Typography variant="body1">{formData.nom_payeur} {formData.prenom_payeur}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                    <Typography variant="subtitle2" color="textSecondary">Téléphone d'urgence</Typography>
                    <Typography variant="body1">{texteTelephones(formData.telephone_urgence)}</Typography>
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
          
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={handleBack} 
              startIcon={<ArrowBackIcon />}
              variant="outlined"
            >
              Modifier
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              color="success"
              sx={{ minWidth: 200 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Confirmer l\'inscription'}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Box sx={{ color: '#2e7d32', mb: 3 }}>
            <svg width="80" height="80" viewBox="0 0 24 24" style={{ fill: 'currentColor' }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </Box>
          
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            Ajout au panier réussi !
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
            Votre inscription à la formation <strong>{formationTitre}</strong> a bien été ajoutée au panier.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
            Vous pouvez continuer à vous inscrire à d'autres formations ou procéder au paiement dans le panier.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/formations"
              variant="outlined"
              sx={{ minWidth: 200 }}
            >
              Autres formations
            </Button>
            
            <Button
              component={Link}
              to="/panier"
              variant="contained"
              color="success"
              sx={{ minWidth: 200 }}
            >
              Voir mon panier
            </Button>
            
            <Button
              component={Link}
              to="/"
              variant="outlined"
              sx={{ minWidth: 200 }}
            >
              Retour à l'accueil
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default InscriptionIndividuelle;
